import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

/**
 * The one data-fetching hook.
 *
 * Returns the same shape for every request in the app — `{ data, error,
 * loading, refetch, setData }` — so `<AsyncBoundary>` can render loading,
 * error and empty states for any resource without knowing what it is. Pages
 * describe *what* they need; they never write a loading flag or a try/catch.
 *
 *   const vehicles = useResource('/api/vehicles', { fleetId });
 *   <AsyncBoundary state={vehicles}>{({ vehicles }) => …}</AsyncBoundary>
 *
 * `params` is compared by value, not identity, so callers can pass an object
 * literal without triggering an infinite re-fetch — the mistake this hook
 * exists to make impossible.
 */
export function useResource(path, params, options = {}) {
  const { enabled = true, pollMs = 0, transform } = options;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled && path));

  const paramsKey = JSON.stringify(params ?? null);
  const abortRef = useRef(null);
  const transformRef = useRef(transform);
  transformRef.current = transform;

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!path || !enabled) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (!quiet) setLoading(true);
      try {
        const result = await api.get(path, JSON.parse(paramsKey) || undefined, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setData(transformRef.current ? transformRef.current(result) : result);
        setError(null);
      } catch (err) {
        if (err.name === 'AbortError' || controller.signal.aborted) return;
        setError(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [path, paramsKey, enabled]
  );

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  // Background refresh keeps counters honest without a spinner flashing on
  // every tick — `quiet` is what makes polling tolerable in a live dashboard.
  useEffect(() => {
    if (!pollMs || !enabled) return undefined;
    const timer = setInterval(() => load({ quiet: true }), pollMs);
    return () => clearInterval(timer);
  }, [pollMs, enabled, load]);

  return {
    data,
    error,
    loading,
    refetch: useCallback(() => load({ quiet: true }), [load]),
    reload: load,
    setData,
  };
}

/**
 * Mutations — the write-side counterpart. Gives every form the same
 * `{ run, pending, error }` shape so no component hand-rolls submit state.
 */
export function useMutation(fn) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(
    async (...args) => {
      setPending(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setPending(false);
      }
    },
    [fn]
  );

  return { run, pending, error, clearError: () => setError(null) };
}

/**
 * A list that is paged and searched on the server.
 *
 * The difference from `useResource` is where the work happens. Fetching 500
 * rows and filtering them in the browser is fine for a depot and hopeless for
 * a national fleet: the payload grows with the tenant, the browser sorts what
 * it should never have received, and the count in the corner is the number of
 * rows that happened to arrive rather than the number that exist.
 *
 * So the search term and the offset are query parameters, the total comes back
 * with the page, and the client holds one screen of rows at a time.
 *
 *   const people = usePagedList('/api/users', { role }, { limit: 50 });
 *   <DataTable rows={people.rows} />
 *   <Pagination page={people.page} onChange={people.setOffset} />
 *
 * The search box is debounced here rather than in each page, and changing any
 * filter returns to the first page — staying on page 7 of a result set that
 * now has two pages is how a list appears empty for no reason.
 */
export function usePagedList(path, params, options = {}) {
  const { limit = 50, key = null, debounceMs = 250, ...rest } = options;

  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  const filterKey = JSON.stringify(params ?? null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), debounceMs);
    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Any change to a filter or the search invalidates the current offset.
  useEffect(() => {
    setOffset(0);
  }, [filterKey, debounced]);

  const resource = useResource(
    path,
    { ...(params || {}), q: debounced || undefined, limit, offset },
    rest
  );

  const rows = key ? resource.data?.[key] : firstArray(resource.data);
  const total = resource.data?.total ?? rows?.length ?? 0;

  return {
    ...resource,
    rows: rows || [],
    total,
    query,
    setQuery,
    setOffset,
    page: {
      limit,
      offset,
      total,
      hasMore: resource.data?.hasMore ?? offset + (rows?.length || 0) < total,
    },
  };
}

/**
 * List endpoints name their array after their noun — `vehicles`, `users`,
 * `entries`. Rather than make every caller repeat that name, find it.
 */
function firstArray(data) {
  if (!data) return null;
  for (const value of Object.values(data)) if (Array.isArray(value)) return value;
  return null;
}
